import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useServiceError } from '@/hooks';

import {
  createSupplierContacts,
  listSupplierContacts,
  removeSupplierContact,
  removeSupplierDetailContact,
  updateSupplierContact,
} from './SupplierContact.service';

import { notification } from 'antd';
import { useIntl } from 'react-intl';
import { CreateSupplierContactsRequest } from './SupplierContact.types';

export function useSupplierContacts(supplierId?: number) {
  const query = useQuery(
    ['supplier/contacts'],
    () => (!!supplierId ? listSupplierContacts(supplierId) : []),
    { enabled: !!supplierId },
  );

  return query;
}

export function useCreateSupplierContacts() {
  const queryClient = useQueryClient();
  const intl = useIntl().formatMessage;
  const mutation = useMutation(
    (data: { obj: CreateSupplierContactsRequest; supplierId: number }) =>
      createSupplierContacts(data.obj, data.supplierId),
    {
      onSuccess() {
        notification.success({
          message: intl({
            id: 'commons.modal.contact.added.message',
          }),
          description: intl({
            id: 'commons.modal.contact.added.description',
          }),
        });
        queryClient.invalidateQueries(['supplier/contacts']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateSupplierContacts() {
  const queryClient = useQueryClient();
  const intl = useIntl().formatMessage;

  const mutation = useMutation(
    (data: {
      obj: CreateSupplierContactsRequest;
      id: number;
      supplierId: number;
    }) => updateSupplierContact(data.obj, data.id, data.supplierId),
    {
      onSuccess() {
        queryClient.invalidateQueries(['supplier/contacts']);

        notification.success({
          message: intl({
            id: 'commons.modal.contact.updated.message',
          }),
          description: intl({
            id: 'commons.modal.contact.updated.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useSupplierContactRemove() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: { supplierId: number; contactId: number }) =>
      removeSupplierContact(data.supplierId, data.contactId),
    {
      onSuccess() {
        queryClient.invalidateQueries(['supplier/contacts']);

        notification.success({
          message: intl({
            id: 'commons.modal.contact.removed.message',
          }),
          description: intl({
            id: 'commons.modal.contact.removed.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useSupplierContactDetailRemove() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: { supplierId: number; detailId: number }) =>
      removeSupplierDetailContact(data.supplierId, data.detailId),
    {
      onSuccess() {
        queryClient.invalidateQueries(['supplier/contacts']);

        notification.success({
          message: intl({
            id: 'commons.modal.contact.removed.message',
          }),
          description: intl({
            id: 'commons.modal.contact.removed.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
